import {
  Text,
  Flex,
  Box,
  Table,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
  Center,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  CreditFacility,
  FormulaEngine,
} from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { AddClassCapitalProviderModal } from '../add/AddClassCapitalProviderModal';
import { DeleteArrayPropertyButton } from '../delete/DeleteArrayPropertyButton';
import {
  TrWithPanel,
  TrWithPanelRow,
  TrWithPanelPanel,
} from './AccordionTableV2';
import { AddIconButton } from 'packages/common-frontend/components/IconButton';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { AddButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddButton';
import { AddPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/add/AddPropertyModal';
import {
  CreditAgreement,
  getIterationKey,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementSectionAccordionTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/CreditAgreementSectionAccordionTable';
import { Property } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/Property';

const createClassDetailsShell = (
  classes: CreditAgreement.DomainEntity.ClassDetailsWithEntities[] = [],
): CreditAgreement.DomainEntity.ClassDetailsWithEntities => {
  const classLabels = _.values(CreditFacility.ClassLabel).filter(
    (label) => label.length === 1, // Only use letter labels when selecting next option
  );

  const lastClass = _.last(classes);

  let currentIdx = -1;
  if (lastClass) {
    const lastLabel = lastClass.classLabel.value.charAt(0); // Check for letter of label
    currentIdx = _.findIndex(classLabels, (label) => label === lastLabel);
  }
  const newLabelIdx = currentIdx + 1;

  finleyAssert(
    newLabelIdx < classLabels.length,
    'Unable to create class details. Class Label is out of range',
  );

  const classLabel = classLabels[newLabelIdx];

  return {
    classLabel: {
      name: 'Class Label',
      value: classLabel,
      type: CreditAgreement.DomainEntity.PropertyType.ClassLabelProperty,
      _isWrittenIn_: true,
    },
    capitalProviders: [],
    attachmentRateLimit: {
      name: 'Attachment Rate Limit',
      value: 'Description for Attachment Rate Limit',
      type:
        CreditAgreement.DomainEntity.PropertyType.AttachmentRateLimitProperty,
      _config_: {
        type: 'formula',
        config: {
          engineVersion: FormulaEngine.FullSupportedFormulaEngines.ExcelEngine1,
          formula: '1',
        },
      },
      _isWrittenIn_: true,
    },
  };
};

const createClassMechanicsShell = (): CreditAgreement.DomainEntity.ClassMechanicsWithEntities => {
  return {
    classes: [createClassDetailsShell()],
  };
};

type ClassDetailsProps = {
  classDetails: CreditAgreement.DomainEntity.ClassDetailsWithEntities;
  wipConfig?: WipConfig;
  basePropertyKey: string;
  classes: CreditAgreement.DomainEntity.ClassDetailsWithEntities[];
};
const ClassDetails = ({
  classDetails,
  wipConfig,
  basePropertyKey,
  classes,
}: ClassDetailsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const sanitizedProperties = _.omit(classDetails, 'capitalProviders');
  const { classLabel, attachmentRateLimit, capitalProviders } = classDetails;

  const accordionTableHeaders: (keyof CreditAgreement.DomainEntity.Property)[] = [
    'name',
    'value',
    'section',
    'description',
  ];

  const showNote =
    _.filter(sanitizedProperties, (property) => {
      if (!property) {
        return false;
      }
      return !_.isNil(property._note_);
    }).length > 0;
  const showIsWrittenIn =
    _.filter(sanitizedProperties, (property) => {
      if (!property) {
        return false;
      }
      return !_.isNil(property._isWrittenIn_);
    }).length > 0;
  if (showNote) {
    accordionTableHeaders.push('_note_');
  }
  if (showIsWrittenIn) {
    accordionTableHeaders.push('_isWrittenIn_');
  }

  // Even column widths with fixed tableLayout so the headers and the accordion rows are always aligned. Dropdown column is fixed to 5%
  // The headers and rows have to be rendered in different tables because an accordion component cannot correctly wrap around an entire table row
  const columnWidth = wipConfig
    ? 95 / accordionTableHeaders.length + 1
    : 95 / accordionTableHeaders.length;

  return (
    <Box mt="20px" mb="20px">
      <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
        <Thead>
          <Tr>
            {_.map(accordionTableHeaders, (header) => {
              return (
                <Th width={`${columnWidth}%`} key={`th-${header}`}>
                  {commonUtils.toTitleCase(header)}
                </Th>
              );
            })}
            {wipConfig && <Th w="32">Action</Th>}
          </Tr>
        </Thead>
        <Tbody>
          <Property
            headers={accordionTableHeaders}
            property={classLabel}
            basePropertyKey={basePropertyKey}
            propertyKey="classLabel"
            isOptional={false}
            columnWidth={columnWidth}
            wipConfig={wipConfig}
          />
          <Property
            headers={accordionTableHeaders}
            property={attachmentRateLimit}
            basePropertyKey={basePropertyKey}
            propertyKey="attachmentRateLimit"
            isOptional={false}
            wipConfig={wipConfig}
            columnWidth={columnWidth}
          />
          <TrWithPanel noPanel={false}>
            {() => (
              <>
                <TrWithPanelRow>
                  <Td colSpan={5}>
                    <Flex alignItems="center">
                      <Text mr="5px">Capital Providers</Text>
                      {wipConfig?.isMutateAllowed(
                        `${basePropertyKey}.capitalProviders`,
                      ) && (
                        <>
                          <AddButton setIsOpen={setIsOpen} />
                          <AddClassCapitalProviderModal
                            wipConfig={wipConfig}
                            setIsOpen={setIsOpen}
                            isOpen={isOpen}
                            basePropertyKey={`${basePropertyKey}.capitalProviders`}
                            classes={classes}
                          />
                        </>
                      )}
                    </Flex>
                  </Td>
                </TrWithPanelRow>
                <TrWithPanelPanel colSpan={5}>
                  <Box>
                    <Center>
                      {_.map(
                        capitalProviders,
                        ({ entity: { name, id } }, idx) => {
                          return (
                            <Flex key={id} alignItems="center">
                              <Text>{name}</Text>
                              {wipConfig?.isMutateAllowed(
                                `${basePropertyKey}.capitalProviders`,
                              ) && (
                                <DeleteArrayPropertyButton
                                  wipConfig={wipConfig}
                                  basePropertyKey={`${basePropertyKey}.capitalProviders`}
                                  idx={idx}
                                />
                              )}
                            </Flex>
                          );
                        },
                      )}
                    </Center>
                  </Box>
                </TrWithPanelPanel>
              </>
            )}
          </TrWithPanel>
        </Tbody>
      </Table>
    </Box>
  );
};

type ClassesProps = {
  classes: CreditAgreement.DomainEntity.ClassDetailsWithEntities[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
};
const Classes = ({ classes, wipConfig, basePropertyKey }: ClassesProps) => {
  return (
    <Box mt="20px" width="100%" p="20px" borderWidth="1px">
      <Flex alignItems="center" mb="2">
        <Text fontWeight="bold" fontSize="20px">
          Classes
        </Text>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            {classes.length < _.values(CreditFacility.ClassLabel).length ? (
              <AddIconButton
                ariaLabel="add"
                label="Add"
                onClick={() => {
                  const classDetails = createClassDetailsShell(classes);

                  const propertySection = getIn(
                    wipConfig.formProps.values,
                    basePropertyKey,
                  );

                  const updatedPropertySetion = [
                    ...propertySection,
                    classDetails,
                  ];
                  wipConfig.formProps.setFieldValue(
                    basePropertyKey,
                    updatedPropertySetion,
                  );
                }}
              />
            ) : undefined}
            {classes.length ? (
              <DeleteArrayPropertyButton
                basePropertyKey={basePropertyKey}
                idx={classes.length - 1}
                wipConfig={wipConfig}
              />
            ) : undefined}
          </>
        )}
      </Flex>
      {_.map(classes, (classDetails, idx) => {
        const id = getIterationKey(classDetails);

        return (
          <ClassDetails
            key={id}
            classDetails={classDetails}
            basePropertyKey={`${basePropertyKey}[${idx}]`}
            wipConfig={wipConfig}
            classes={classes}
          />
        );
      })}
    </Box>
  );
};

type ClassMechanicsProps = {
  classMechanics:
    | CreditAgreement.DomainEntity.ClassMechanicsWithEntities
    | undefined;
  wipConfig?: WipConfig;
  structure: CreditFacility.CreditFacilityStructureType;
};
export const ClassMechanics = ({
  classMechanics,
  wipConfig,
  structure,
}: ClassMechanicsProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const basePropertyKey = 'classMechanics';

  useEffect(() => {
    // if in wip mode, and class mechanics exists, but the facility is not classed, let's remove it since we don't need it anymore
    if (
      structure !== CreditFacility.CreditFacilityStructureType.Classed &&
      wipConfig &&
      classMechanics
    ) {
      const updatedWip = _.omit(wipConfig.formProps.values, basePropertyKey);
      wipConfig.formProps.setValues(updatedWip);
    }

    // if in wip mode, and class mechanics does not exist, but the facility is classed, let's create the shell
    if (
      structure === CreditFacility.CreditFacilityStructureType.Classed &&
      wipConfig &&
      !classMechanics
    ) {
      const classMechanicsShell = createClassMechanicsShell();
      wipConfig.formProps.setFieldValue(basePropertyKey, classMechanicsShell);
    }
  }, [classMechanics, structure, wipConfig]);

  if (!classMechanics) {
    return null;
  }

  const { classes } = classMechanics;

  const sanitizedProperties = _.omit(classMechanics, 'classes');

  return (
    <Box mt="40px">
      <StickyHeaderSection labelText="Class Mechanics">
        <Flex alignItems="center" mb="2">
          <Text fontWeight="bold" fontSize="20px">
            Properties
          </Text>
          {wipConfig?.isMutateAllowed(basePropertyKey) && (
            <>
              <AddButton setIsOpen={setIsOpen} />
              <AddPropertyModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                propertyMap={getIn(
                  wipConfig.creditAgreementFullArtifactPropertyMap,
                  basePropertyKey,
                )}
              />
            </>
          )}
        </Flex>
        <CreditAgreementSectionAccordionTable
          properties={sanitizedProperties}
          basePropertyKey={`${basePropertyKey}`}
          wipConfig={wipConfig}
        />
        <Classes
          classes={classes}
          basePropertyKey={`${basePropertyKey}.classes`}
          wipConfig={wipConfig}
        />
      </StickyHeaderSection>
    </Box>
  );
};
